import type { EventDto, EventSummaryDto, Totals } from '@pietrochip/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as api from '../api/endpoints';
import { describeError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useToasts } from '../components/Toasts';
import { EmptyState, Field, Modal, Notice, PercentText, ProgressBar } from '../components/ui';
import { eventDate } from '../lib/format';
import { useFallbackPoll } from '../realtime/useFallbackPoll';
import { useGlobalStream } from '../realtime/useGlobalStream';
import { EventFormModal } from '../components/EventFormModal';

export function EventsPage() {
  const { user } = useAuth();
  const { push } = useToasts();
  const navigate = useNavigate();

  const [events, setEvents] = useState<EventSummaryDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<EventDto | null>(null);
  const [deleting, setDeleting] = useState<EventSummaryDto | null>(null);
  /** La sezione degli archiviati parte chiusa: la home si tiene pulita. */
  const [showArchived, setShowArchived] = useState(false);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    try {
      const { events: list } = await api.listEvents();
      setEvents(list);
      setError(null);
    } catch (cause) {
      setError(describeError(cause));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Gli aggiornamenti in tempo reale si applicano alla lista già in memoria.
   *
   * Un evento creato da un altro amministratore compare da solo; i suoi totali
   * arrivano subito dopo sul canale globale, quindi la card nasce con i
   * conteggi a zero e si riempie un istante più tardi.
   */
  const streamStatus = useGlobalStream({
    onTotals: (eventId, totals) =>
      setEvents((current) => patchEvent(current, eventId, (event) => ({ ...event, totals }))),

    onCreated: (event) => {
      setEvents((current) =>
        current === null
          ? current
          : current.some((entry) => entry.id === event.id)
            ? current
            : [...current, { ...event, totals: emptyTotals() }],
      );
    },

    onUpdated: (event) =>
      setEvents((current) => patchEvent(current, event.id, (entry) => ({ ...entry, ...event }))),

    onDeleted: (eventId) =>
      setEvents((current) => current?.filter((entry) => entry.id !== eventId) ?? current),
  });

  /**
   * Dopo una riconnessione si rilegge tutto.
   *
   * Il canale globale trasmette solo i totali che cambiano, e un buco di rete
   * può averne fatto perdere qualcuno: alla riapertura conviene richiedere
   * l'elenco intero invece di fidarsi di quello che si ha in memoria.
   */
  const previousStatus = useRef(streamStatus);
  useEffect(() => {
    if (previousStatus.current === 'reconnecting' && streamStatus === 'open') void load();
    previousStatus.current = streamStatus;
  }, [streamStatus, load]);

  // La richiamata del ripiego è richiamata da un timer: legge l'elenco
  // corrente attraverso una ref invece che dalla copia catturata.
  const eventsRef = useRef(events);
  eventsRef.current = events;

  /**
   * Ricaricamento di riserva dell'elenco.
   *
   * Qui non serve la rotta della versione: l'elenco sono poche righe, con solo
   * i totali per evento, e si può richiedere per intero.
   *
   * A differenza di `load`, un giro perso **non** accende l'avviso: l'elenco
   * che c'è resta valido, e una rete che va e viene non deve riempire la home
   * di messaggi rossi. L'errore si mostra solo finché non c'è ancora niente da
   * mostrare.
   */
  const poll = useCallback(async () => {
    try {
      const { events: list } = await api.listEvents();
      setEvents(list);
      setError(null);
    } catch (cause) {
      if (eventsRef.current === null) setError(describeError(cause));
    }
  }, []);

  useFallbackPoll({ active: true, status: streamStatus, poll });

  const sorted = useMemo(() => {
    if (events === null) return null;
    // Prima l'evento più recente: è quello su cui si sta lavorando adesso.
    return [...events].sort((a, b) => {
      const byDate = (b.date ?? '').localeCompare(a.date ?? '');
      if (byDate !== 0) return byDate;
      return b.createdAt.localeCompare(a.createdAt);
    });
  }, [events]);

  /**
   * Gli eventi si dividono in due elenchi con lo stesso ordinamento.
   *
   * L'archiviato non si cancella dalla lista in memoria: resta qui e si sposta
   * solo di sezione. Così ripristinarlo è immediato, e un messaggio del socket
   * che lo riporta attivo (`archivedAt` di nuovo `null`) lo fa ricomparire
   * senza rileggere niente.
   */
  const active = useMemo(() => sorted?.filter((event) => event.archivedAt === null) ?? null, [sorted]);
  const archived = useMemo(() => sorted?.filter((event) => event.archivedAt !== null) ?? [], [sorted]);

  /**
   * Archivia e ripristina.
   *
   * Sono la stessa operazione al contrario, e il server risponde con l'evento
   * aggiornato: una sola patch della lista, e la card cambia sezione da sola.
   * Se il server dice di no, non si tocca niente — lo stato resta quello.
   */
  async function onArchive(event: EventSummaryDto, archivedNow: boolean) {
    try {
      const { event: updated } = archivedNow
        ? await api.archiveEvent(event.id)
        : await api.unarchiveEvent(event.id);

      setEvents((current) => patchEvent(current, updated.id, (entry) => ({ ...entry, ...updated })));
      push({
        tone: 'neutral',
        message: archivedNow
          ? `Evento «${updated.name}» archiviato.`
          : `Evento «${updated.name}» riportato negli eventi attivi.`,
      });
    } catch (cause) {
      push({ tone: 'danger', message: describeError(cause) });
    }
  }

  async function onDelete(event: EventSummaryDto) {
    try {
      await api.deleteEvent(event.id);
      setEvents((current) => current?.filter((entry) => entry.id !== event.id) ?? current);
      setDeleting(null);
      push({ tone: 'neutral', message: `Evento «${event.name}» eliminato.` });
    } catch (cause) {
      push({ tone: 'danger', message: describeError(cause) });
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Eventi</h1>
          <p className="page-head__meta">
            {active === null
              ? 'Caricamento…'
              : `${active.length === 1 ? '1 evento' : `${active.length} eventi`}${
                  archived.length > 0
                    ? ` · ${archived.length === 1 ? '1 archiviato' : `${archived.length} archiviati`}`
                    : ''
                }`}
          </p>
        </div>

        {isAdmin && (
          <div className="page-head__actions">
            <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
              Nuovo evento
            </button>
          </div>
        )}
      </div>

      {error && <Notice tone="danger">{error}</Notice>}

      {active === null && !error && <p className="muted">Caricamento degli eventi…</p>}

      {active !== null && active.length === 0 && archived.length === 0 && (
        <div className="card">
          <EmptyState
            title="Nessun evento"
            hint={
              isAdmin
                ? 'Crea il primo evento, poi importa l’elenco dei chip consegnati.'
                : 'Un amministratore non ha ancora creato nessun evento.'
            }
          />
        </div>
      )}

      {active !== null && active.length > 0 && (
        <div className="events">
          {active.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              isAdmin={isAdmin}
              onRename={() => setRenaming(event)}
              onDelete={() => setDeleting(event)}
              onArchive={() => void onArchive(event, true)}
            />
          ))}
        </div>
      )}

      {/*
        Gli archiviati stanno in fondo, chiusi. Non spariscono — un evento
        archiviato si riapre e si rilegge — ma non occupano la prima schermata,
        che è il punto dell'archiviazione.
      */}
      {archived.length > 0 && (
        <section className="stack" style={{ gap: 10 }}>
          <button
            type="button"
            className="btn btn--quiet"
            style={{ alignSelf: 'flex-start' }}
            aria-expanded={showArchived}
            onClick={() => setShowArchived((current) => !current)}
          >
            {showArchived
              ? 'Nascondi archiviati'
              : `Mostra archiviati (${archived.length})`}
          </button>

          {showArchived && (
            <div className="events events--archived">
              {archived.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  isAdmin={isAdmin}
                  onRename={() => setRenaming(event)}
                  onDelete={() => setDeleting(event)}
                  onUnarchive={() => void onArchive(event, false)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {creating && (
        <EventFormModal
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={(event) => {
            setCreating(false);
            push({ tone: 'neutral', message: `Evento «${event.name}» creato.` });
            // Si entra subito nell'evento: è quello che si vuole fare dopo
            // averlo creato, e il passo successivo è importare i chip.
            navigate(`/eventi/${event.id}`);
          }}
        />
      )}

      {renaming && (
        <EventFormModal
          mode="rename"
          event={renaming}
          onClose={() => setRenaming(null)}
          onSaved={(event) => {
            setEvents((current) => patchEvent(current, event.id, (entry) => ({ ...entry, ...event })));
            setRenaming(null);
          }}
        />
      )}

      {deleting && (
        <DeleteEventModal
          event={deleting}
          onClose={() => setDeleting(null)}
          onConfirm={() => onDelete(deleting)}
        />
      )}
    </>
  );
}

function EventCard({
  event,
  isAdmin,
  onRename,
  onDelete,
  onArchive,
  onUnarchive,
}: {
  event: EventSummaryDto;
  isAdmin: boolean;
  onRename: () => void;
  onDelete: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
}) {
  const { totals } = event;
  const complete = totals.total > 0 && totals.missing === 0;
  const isArchived = event.archivedAt !== null;

  return (
    <div className={`event-card ${isArchived ? 'event-card--archived' : ''}`}>
      <Link to={`/eventi/${event.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
        <div className="stack" style={{ gap: 10 }}>
          <div>
            <div className="event-card__name">
              {event.name}
              {isArchived && <span className="event-card__badge">Archiviato</span>}
            </div>
            <div className="event-card__date">
              {event.date ? eventDate(event.date) : 'Senza data'}
              {event.notes ? ` · ${event.notes}` : ''}
            </div>
          </div>

          <ProgressBar fraction={totals.percent} label={`Avanzamento di ${event.name}`} />

          <div className="event-card__stats">
            <span>
              <strong className="mono">{totals.returned}</strong>
              <span className="faint"> / {totals.total} restituiti</span>
            </span>
            <span className="event-card__percent">
              {totals.total === 0 ? '—' : <PercentText fraction={totals.percent} digits={0} />}
            </span>
          </div>

          <div className="row" style={{ gap: 12, fontSize: '0.8125rem' }}>
            <span className="state state--missing">
              <span className="state__mark" aria-hidden="true" />
              {totals.missing} mancanti
            </span>
            {complete && <span className="state state--returned">Chiuso</span>}
          </div>
        </div>
      </Link>

      {isAdmin && (
        <div className="event-card__actions">
          <button type="button" className="btn btn--sm btn--quiet" onClick={onRename}>
            Rinomina
          </button>
          {/* Archiviare e ripristinare sono lo stesso pulsante: cambia la
              direzione. In una card archiviata non c'è niente da archiviare. */}
          {isArchived ? (
            <button type="button" className="btn btn--sm btn--quiet" onClick={onUnarchive}>
              Ripristina
            </button>
          ) : (
            <button type="button" className="btn btn--sm btn--quiet" onClick={onArchive}>
              Archivia
            </button>
          )}
          <span className="shell__spacer" />
          <button type="button" className="btn btn--sm btn--quiet" onClick={onDelete}>
            Elimina
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Conferma di eliminazione.
 *
 * Non un `confirm()` del browser ma una finestra vera, con il nome dell'evento
 * da digitare: l'operazione porta via chip, registro e riepiloghi, ed è
 * l'unica cosa in PietroChip che non si può disfare. Il nome da scrivere è
 * anche il modo per impedire che un tocco distratto su un tablet la esegua.
 */
function DeleteEventModal({
  event,
  onClose,
  onConfirm,
}: {
  event: EventSummaryDto;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const matches = typed.trim() === event.name;

  return (
    <Modal
      title="Eliminare l’evento?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={!matches || busy}
            onClick={async () => {
              setBusy(true);
              await onConfirm();
              setBusy(false);
            }}
          >
            {busy ? 'Eliminazione…' : 'Elimina definitivamente'}
          </button>
        </>
      }
    >
      <Notice tone="danger">
        Vengono eliminati <strong>{event.totals.total} chip</strong>, le loro restituzioni e tutto il
        registro delle operazioni. Non si può annullare.
      </Notice>

      <Field label={`Scrivi «${event.name}» per confermare`}>
        {({ id, ...aria }) => (
          <input
            {...aria}
            id={id}
            className="input"
            value={typed}
            onChange={(inputEvent) => setTyped(inputEvent.target.value)}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
          />
        )}
      </Field>
    </Modal>
  );
}

function patchEvent(
  events: EventSummaryDto[] | null,
  eventId: string,
  patch: (event: EventSummaryDto) => EventSummaryDto,
): EventSummaryDto[] | null {
  if (events === null) return events;
  let changed = false;
  const next = events.map((event) => {
    if (event.id !== eventId) return event;
    changed = true;
    return patch(event);
  });
  return changed ? next : events;
}

function emptyTotals(): Totals {
  return { total: 0, returned: 0, missing: 0, percent: 0 };
}
